import { faCalendar } from '@fortawesome/free-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Select } from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { useForm } from '@mantine/form'
import { isNil } from '@semios/app-platform-common'
import type { TLngLat } from 'App/Map/types'
import { RequiredMarker } from 'App/Map/UserSettingsMenu/Shared/Presets/RequiredMarker/RequiredMarker'
import { ModalDrawer } from 'components/ModalDrawer/ModalDrawer'
import { NumberInput } from 'components/NumberInput/NumberInput'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { useEffect } from 'react'
import { colors } from 'settings/colors'
import { SharedSettings } from 'settings/SharedSettings'
import { detailsPanelStore } from 'stores/detailsPanelStore/detailsPanelStore'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TPointCategory } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import { apiFetch } from 'utils/apiFetch'
import { doesSelectedPointHaveValueTypes } from 'utils/doesSelectedFieldAssetHaveValueTypes'
import { getIsoWithoutTheZ } from 'utils/getIsoWithoutTheZ'
import { showNotification } from 'utils/showNotification'
import { zodResolver } from 'utils/zodResolver'
import { z } from 'zod'
import { valueTypes } from '../../StackemCharts/_utils/by-domain/fruitGrowth/fruitGrowth'

const pointCategory: TPointCategory = 'fruitGrowthPoint'

type TargetSizeUnitType = 'INCH' | 'MILLIMETER'

type TFruitGrowthSettings = {
  timezone: string
  defaultTargetSize: number | null
  defaultTargetDate: string | null
  defaultTargetSizeUnit: TargetSizeUnitType | null
}

const refreshTheDetailsPanelData = () =>
  detailsPanelStore.setState((s) => ({ ...s, keyForRefreshing: new Date().toISOString() }))

export const FruitGrowthSettingsModal = ({
  timezone,
  defaultTargetSize,
  defaultTargetDate,
  defaultTargetSizeUnit,
}: TFruitGrowthSettings) => {
  const fruitGrowthSettingsOpened = detailsPanelStore.useSelector((s) => {
    return s.fruitGrowthSettingsOpened
  })

  const setFruitGrowthSettingsOpened = (value: boolean) => {
    detailsPanelStore.setState((prev) => {
      return {
        ...prev,
        fruitGrowthSettingsOpened: value,
      }
    })
  }

  const defaultUnit = userDetailsStore.useSelector((s): TargetSizeUnitType =>
    s.rain === 'IMPERIAL' ? 'INCH' : 'MILLIMETER',
  )

  const minTimeMoment = moment.tz(timezone)

  const schema = z.object({
    targetSize: z
      .number({
        error: (issue) =>
          issue.input === undefined
            ? translate.phrases.validation('{{label}} is required', {
                label: translate.phrases.banyanApp('Target Size'),
              })
            : undefined,
      })
      .positive({
        message: translate.phrases.validation('{{label}} must be > {{value}}', {
          label: translate.phrases.banyanApp('Target Size'),
          value: 0,
        }),
      }),
    targetDate: z
      .date({
        error: (issue) =>
          issue.input === undefined
            ? translate.phrases.validation('{{label}} is required', {
                label: translate.phrases.banyanApp('Target Date'),
              })
            : undefined,
      })
      .min(minTimeMoment.toDate(), {
        message: translate.phrases.validation('{{label}} must be >= {{value}}', {
          label: translate.phrases.banyanApp('Target Date'),
          value: translate.dates.format(minTimeMoment, 'ddd, MMM D, YYYY h:mm a (z)'),
        }),
      }) //Target date must be in the future
      .max(moment.tz(timezone).add(1, 'year').toDate()), //Target date must not be more than 1 year from now
    targetSizeUnit: z.enum(['INCH', 'MILLIMETER'], {
      error: (issue) =>
        issue.input === undefined
          ? translate.phrases.validation('{{label}} is required', {
              label: translate.phrases.banyanApp('Target Size Unit'),
            })
          : undefined,
    }),
  })

  const form = useForm({
    validate: zodResolver(schema),
    validateInputOnBlur: true,
  })

  useEffect(() => {
    form.setValues({
      targetSize: !isNil(defaultTargetSize) ? defaultTargetSize : undefined,
      targetDate: !isNil(defaultTargetDate)
        ? new Date(getIsoWithoutTheZ(moment.tz(defaultTargetDate, timezone)))
        : undefined,
      targetSizeUnit: !isNil(defaultTargetSizeUnit) ? defaultTargetSizeUnit : defaultUnit,
    })
  }, [fruitGrowthSettingsOpened, defaultTargetSize, defaultTargetDate, defaultTargetSizeUnit, defaultUnit])

  const { selectedFruitDendrometerStation, propertyId } = selectedFieldAssetsStore.useSelector((s) => ({
    propertyId: s.property,
    selectedFruitDendrometerStation: s[pointCategory],
  }))

  const selectedDendrometerName = fieldAssetStore.useSelector(
    (s) =>
      s?.properties?.[Number(propertyId)]?.points?.[selectedFruitDendrometerStation as TLngLat]?.name ??
      'Unnamed Station', // note: not wrapped in t, for consistency with the dropdowns
  )

  if (
    !selectedFruitDendrometerStation ||
    !doesSelectedPointHaveValueTypes({
      valuesTimeseries: valueTypes,
      pointCategory,
    })
  )
    return null

  const handleUpdate = async () => {
    const newSettingsValues = form.values

    const userTimezoneFormattedDate = moment
      .tz(newSettingsValues.targetDate, moment.tz.guess())
      .format('YYYY-MM-DD')

    try {
      const response = await apiFetch({
        url: '/field-asset-settings-set',
        body: {
          fruitGrowthSettingsUpdate: [
            {
              fruitDendrometerLngLat: selectedFruitDendrometerStation as string,
              targetDate: moment.tz(userTimezoneFormattedDate, timezone).toISOString(),
              targetSizeValue: newSettingsValues.targetSize,
              targetSizeUnit: newSettingsValues.targetSizeUnit,
            },
          ],
        },
      })

      if (
        Array.isArray(response?.fruitGrowthSettingsUpdate) &&
        response.fruitGrowthSettingsUpdate[0].fruitDendrometerLngLat
      ) {
        if (+new Date(detailsPanelStore.getState().dateTo) < +new Date(newSettingsValues.targetDate)) {
          detailsPanelStore.setState((s) => ({
            ...s,
            dateTo: getIsoWithoutTheZ(moment.tz(userTimezoneFormattedDate, timezone).endOf('day')),
          }))
        }

        setFruitGrowthSettingsOpened(false)

        showNotification({
          message: translate.phrases.banyanApp('Successfully updated settings'),
          type: 'success',
        })

        refreshTheDetailsPanelData()
      } else {
        showNotification({
          message: translate.phrases.banyanApp('Failed to update fruit growth setting'),
          type: 'error',
        })
      }
    } catch (error) {
      showNotification({
        message: translate.phrases.banyanApp('Failed to update fruit growth setting'),
        type: 'error',
      })
    }
  }

  const handleDelete = async () => {
    try {
      const response = await apiFetch({
        url: '/field-asset-settings-set',
        body: {
          fruitGrowthSettingsDelete: [{ fruitDendrometerLngLat: selectedFruitDendrometerStation as string }],
        },
      })

      if (
        Array.isArray(response?.fruitGrowthSettingsDelete) &&
        response.fruitGrowthSettingsDelete[0].fruitDendrometerLngLat
      ) {
        setFruitGrowthSettingsOpened(false)

        showNotification({
          message: translate.phrases.banyanApp('Successfully deleted settings'),
          type: 'success',
        })

        refreshTheDetailsPanelData()
      } else {
        showNotification({
          message: translate.phrases.banyanApp('Failed to delete fruit growth setting'),
          type: 'error',
        })
      }
    } catch (error) {
      showNotification({
        message: translate.phrases.banyanApp('Failed to delete fruit growth setting'),
        type: 'error',
      })
    }
  }

  const deleteButtonDisabled =
    isNil(defaultTargetSize) || isNil(defaultTargetDate) || isNil(defaultTargetSizeUnit)

  const formUnchanged =
    form.values.targetSize === defaultTargetSize &&
    form.values.targetSizeUnit === defaultTargetSizeUnit &&
    moment.tz(form.values.targetDate, timezone).toISOString() === defaultTargetDate

  const updateButtonDisabled = !form.isValid() || !form.isTouched() || formUnchanged

  return (
    <ModalDrawer
      title={translate.phrases.banyanApp('Edit Fruit Growth Settings')}
      opened={fruitGrowthSettingsOpened}
      onClose={() => {
        form.reset()

        setFruitGrowthSettingsOpened(false)
      }}
      primaryButtonText={translate.phrases.banyanApp('Save Changes')}
      primaryButtonOnPress={handleUpdate}
      primaryButtonDisabled={updateButtonDisabled}
      secondaryButtonText={translate.phrases.banyanApp('Delete')}
      secondaryButtonDisabled={deleteButtonDisabled}
      secondaryButtonOnPress={handleDelete}
      zIndex={SharedSettings.DEFAULT_MODAL_DRAWER_Z_INDEX + 1}
    >
      <div css={{ padding: 20 }}>
        <div css={{ fontWeight: 'bold', marginTop: 20 }}>{selectedDendrometerName}</div>

        <div css={{ marginTop: 30 }}>
          <form onSubmit={form.onSubmit(() => undefined)}>
            <div css={{ display: 'flex', flexDirection: 'row' }}>
              <div css={{ fontWeight: 'bold', marginRight: 5 }}>
                {translate.phrases.banyanApp('Target Size')}
              </div>
              <RequiredMarker />
            </div>
            <div css={{ display: 'flex', flexDirection: 'row', marginTop: 5 }}>
              <NumberInput
                decimalScale={2}
                fixedDecimalScale
                style={{ width: 110 }}
                step={0.01}
                stepHoldDelay={500}
                stepHoldInterval={100}
                {...form.getInputProps('targetSize')}
              />
              <div css={{ marginLeft: 25 }}>
                <Select
                  data={[
                    { value: 'INCH', label: translate.phrases.banyanApp('Inch') },
                    { value: 'MILLIMETER', label: translate.phrases.banyanApp('Millimeter') },
                  ]}
                  style={{ width: 165 }}
                  {...form.getInputProps('targetSizeUnit')}
                  value={form.values?.targetSizeUnit}
                  onChange={(newTargetSizeUnit) => {
                    if (newTargetSizeUnit === null) return

                    form.setFieldValue('targetSizeUnit', newTargetSizeUnit as TargetSizeUnitType)
                  }}
                />
              </div>
            </div>
            <div css={{ marginTop: 30 }}>
              <div css={{ display: 'flex', flexDirection: 'row' }}>
                <div css={{ fontWeight: 'bold', marginRight: 5 }}>
                  {translate.phrases.banyanApp('Target Date')}
                </div>
                <RequiredMarker />
              </div>
              <DatePickerInput
                rightSection={
                  <FontAwesomeIcon css={{ color: colors.grey800, fontSize: 16 }} icon={faCalendar} />
                }
                style={{ width: 270, marginTop: 5 }}
                {...form.getInputProps('targetDate')}
                value={!isNil(form.values?.targetDate) ? form.values?.targetDate : undefined}
                onChange={(newTargetDate) =>
                  !isNil(newTargetDate) &&
                  form.setFieldValue(
                    'targetDate',
                    moment.tz(newTargetDate, moment.tz.guess()).startOf('day').toDate(),
                  )
                }
                minDate={moment.tz(timezone).add(1, 'day').toDate()}
                maxDate={moment.tz(timezone).add(1, 'years').toDate()}
                valueFormat={translate.dates.getMomentFormat('MMM D, YYYY')}
              />
            </div>
          </form>
        </div>
      </div>
    </ModalDrawer>
  )
}
